import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, formatNumber, UserSettings } from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  LovValues,
  TemplateFieldsDisplayFn,
} from '../../../../../models/template.model';
import { sortAttributesByFieldId } from '../../../utils/argument-sorting';
import {
  displayFnWrapper,
  formatYesOrNoValues,
  isDash,
  mapAndFormatDynamicValue,
} from '../../../utils/dynamic-data-retrievers';
import {
  currencyLov,
  formatCompanyRoleVals,
  PropertyType,
  propertyTypeDisplayFnFactory,
  valueWithUomPrefix,
} from '../us.common';

function priceAskingRate(
  record: ReportRecord,
  minMonthlyRate: DisplayFnArgumentsInfo,
  maxMonthlyRate: DisplayFnArgumentsInfo,
  minYearlyRate: DisplayFnArgumentsInfo,
  maxYearlyRate: DisplayFnArgumentsInfo,
  currencyLOV: LovValues[],
  propertyType: PropertyType
) {
  const isYearlyPref = UserSettings.isYearlyPreference();
  const askingRateVal = !!isYearlyPref
    ? getMinMaxRateInfo(record, propertyType, minYearlyRate, maxYearlyRate, currencyLOV)
    : getMinMaxRateInfo(record, propertyType, minMonthlyRate, maxMonthlyRate, currencyLOV);
  const checkAskingRateVal = askingRateVal != null && !isDash(askingRateVal);
  return checkAskingRateVal ? `${askingRateVal} (Lease)` : null;
}

function getMinMaxRateInfo(
  record: ReportRecord,
  propertyType: PropertyType,
  minRateField: DisplayFnArgumentsInfo,
  maxRateField: DisplayFnArgumentsInfo,
  currencyLOV: LovValues[]
) {
  const minRateValue = valueWithUomPrefix(record, propertyType, minRateField, currencyLOV);
  const maxRateValue = valueWithUomPrefix(record, propertyType, maxRateField, currencyLOV);
  let minMaxRate: string | string[] = '-';
  const checkMinRateVal = minRateValue != null && !isDash(minRateValue);
  const checkMaxRateVal = maxRateValue != null && !isDash(maxRateValue);
  if (checkMinRateVal && checkMaxRateVal) {
    minMaxRate = `${minRateValue} - ${maxRateValue}`;
  } else if (checkMinRateVal || checkMaxRateVal) {
    minMaxRate = checkMinRateVal ? minRateValue : maxRateValue;
  }
  return minMaxRate;
}

export function calcPercentage(dividend: string | number, divisor: string | number, digits): string {
  divisor = typeof divisor === 'number' ? divisor : parseInt(divisor);
  dividend = typeof dividend === 'number' ? dividend : parseInt(dividend);
  const val: number = divisor !== 0 ? (dividend / divisor) * 100 : 0;
  const result = formatNumber(val, 'en-US', digits);
  return result;
}

export function getFloorSuiteVals(record: ReportRecord, floor: DisplayFnArgumentsInfo, suite: DisplayFnArgumentsInfo) {
  const floorVal = mapAndFormatDynamicValue(record, floor);
  const suiteVal = mapAndFormatDynamicValue(record, suite);
  return `${floorVal} | ${suiteVal}`;
}

// DISPLAY FUNCTIONS MAPPING
export function US_AVAILABILITY_COMMON_HEADER_FIELDS(propertyType: PropertyType): TemplateFieldsDisplayFn {
  const displayFnPropertyType = propertyTypeDisplayFnFactory(propertyType);
  function formatAskingRateValues(record, options, minMonthly, maxMonthly, minYearly, maxYearly, rateType) {
    const lov$ = currencyLov(options);
    return lov$.pipe(
      map((currencyLOV: LovValues[]) => {
        const rateTypeVal = displayFnPropertyType(record, options, rateType);
        const isYearlyPref = UserSettings.isYearlyPreference();
        const suffix = isYearlyPref ? 'Yr' : 'Mo';
        const askingRateVal = isYearlyPref
          ? getMinMaxRateInfo(record, propertyType, minYearly, maxYearly, currencyLOV)
          : getMinMaxRateInfo(record, propertyType, minMonthly, maxMonthly, currencyLOV);
        const result = `${askingRateVal} ${rateTypeVal}/${suffix}`;
        return result;
      })
    );
  }
  return {
    26: {
      // 26 - Property Name
      displayFn: (record: ReportRecord, _, arg) => {
        const values = concatValuesBy(defaultTxt, mapAndFormatDynamicValue(record, arg));
        return values;
      },
    },
    27: {
      // 27 - Property Address
      displayFn: (record: ReportRecord, options, ...args) => {
        const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
        const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
        return concatValuesBy(', ', ...values).replace(/,(?=[^,]*$)/, ''); // Removing last comma before PIN
      },
      routeFn: ({ data }) => {
        const entityRecord = Array.isArray(data) ? data[0] : data;
        return ['/records', 'availability', digger(entityRecord, 'entity.id'), 'details'];
      },
    },
    50: {
      displayFn: (record: ReportRecord, _, market, submarket, district, neighborhood) => {
        const marketVal = mapAndFormatDynamicValue(record, market);
        const submarketVal = mapAndFormatDynamicValue(record, submarket);
        const districtVal = mapAndFormatDynamicValue(record, district);
        const neighborhoodVal = mapAndFormatDynamicValue(record, neighborhood);
        const locationValues = [marketVal, submarketVal, districtVal, neighborhoodVal].filter(
          val => !isDash(val) && val != null
        );
        return concatValuesBy(' - ', ...locationValues);
      },
    }, // 50 - Location
    178: { displayFn: displayFnWrapper }, // 178 - Size
    182: {
      displayFn: (record: ReportRecord, options, nra, avail, vacant) => {
        let nraVal = mapAndFormatDynamicValue(record, nra, undefined, false) as string;
        let availVal = displayFnPropertyType(record, options, avail) as string;
        let vacantVal = displayFnPropertyType(record, options, vacant) as string;
        nraVal = isDash(nraVal) || nraVal == null ? '0' : nraVal;
        availVal = isDash(availVal) || availVal == null ? '0' : availVal;
        vacantVal = isDash(vacantVal) || vacantVal == null ? '0' : vacantVal;
        const availPer = `${calcPercentage(availVal, nraVal, nra.dataFormat)} %`;
        const vacantPer = `${calcPercentage(vacantVal, nraVal, nra.dataFormat)} %`;
        return concatValuesBy(' | ', availPer, vacantPer);
      },
    }, // 182 - Avail | Vacant
    184: {
      displayFn: displayFnPropertyType,
    }, // 184 - Space Available
    185: {
      displayFn: formatAskingRateValues,
    }, // 185 - Asking Rate (for Direct Lease)
    186: {
      displayFn: formatAskingRateValues,
    }, // 186 - Asking Rate (for SubLease)
    189: { displayFn: displayFnWrapper }, // 189 - Asking Rate Type for Direct Lease)
  };
}

export function US_AVAILABILITY_COMMON_BODY_FIELDS(propertyType: PropertyType): TemplateFieldsDisplayFn {
  const displayFnPropertyType = propertyTypeDisplayFnFactory(propertyType);
  function formatPrice(
    record: ReportRecord,
    options: DisplayFunctionOptions,
    askingSalePrice: DisplayFnArgumentsInfo,
    minMonthlyRate: DisplayFnArgumentsInfo,
    maxMonthlyRate: DisplayFnArgumentsInfo,
    minYearlyRate: DisplayFnArgumentsInfo,
    maxYearlyRate: DisplayFnArgumentsInfo
  ) {
    return currencyLov(options).pipe(
      map((currencyLOV: LovValues[]) => {
        const askingPriceVal = valueWithUomPrefix(record, propertyType, askingSalePrice, currencyLOV);
        const askingSalePriceVal = !!askingPriceVal && !isDash(askingPriceVal) ? `${askingPriceVal} (Sale)` : null;
        const askingRateVal = priceAskingRate(
          record,
          minMonthlyRate,
          maxMonthlyRate,
          minYearlyRate,
          maxYearlyRate,
          currencyLOV,
          propertyType
        );
        let price = '-';
        if (askingSalePriceVal != null && askingRateVal != null) {
          price = `${askingSalePriceVal} | ${askingRateVal}`;
        } else if (askingSalePriceVal != null || askingRateVal != null) {
          price = askingSalePriceVal != null ? askingSalePriceVal : askingRateVal;
        }
        return price;
      })
    );
  }
  return {
    193: { displayFn: displayFnWrapper }, // 193 - Available Space
    194: {
      displayFn: (record: ReportRecord, _, maxSpace, minSpace) => {
        const maxVal = mapAndFormatDynamicValue(record, maxSpace);
        const minVal = mapAndFormatDynamicValue(record, minSpace);
        const values = concatValuesBy(' - ', minVal, maxVal);
        return values;
      },
    }, // 194 - Min - Max Space
    195: {
      displayFn: (record, options, type, subtype, classType) => {
        const propertyDisplayFnWrapper = propertyTypeDisplayFnFactory(propertyType);
        const propertyTypeVal = propertyDisplayFnWrapper(record, options, type);
        const subtypeVal = propertyDisplayFnWrapper(record, options, subtype);
        const classTypeVal = propertyDisplayFnWrapper(record, options, classType);
        const values = concatValuesBy(' | ', propertyTypeVal, subtypeVal, classTypeVal);
        return values;
      },
    }, // 195 - Building Info
    196: { displayFn: formatPrice }, // 196 - Price
    199: { displayFn: displayFnPropertyType }, // 199 - Lease Type
    200: { displayFn: displayFnPropertyType }, // 200 - Available On
    201: {
      displayFn: (record: ReportRecord, options, vacant) => {
        const vacantVal = mapAndFormatDynamicValue(record, vacant) as string;
        const result = formatYesOrNoValues(vacantVal, options);
        return result;
      },
    }, // 201 - Vacant (Y/N)
    187: {
      displayFn: (record: ReportRecord, _, arg) => {
        const result = formatCompanyRoleVals(record, arg, 187);
        return result;
      },
    }, // 187 - Sub Lease Rep
    188: {
      displayFn: (record: ReportRecord, _, arg) => {
        const result = formatCompanyRoleVals(record, arg, 188);
        return result;
      },
    }, // 188 - Sub Lessor
    202: {
      displayFn: (record: ReportRecord, _, compArgs, repArgs) => {
        const companyName = formatCompanyRoleVals(record, compArgs, 202);
        const displayName = formatCompanyRoleVals(record, repArgs, 203);
        return [companyName, displayName].join(' | ');
      },
    }, // 202 - Listing Rep
    203: {
      displayFn: (record: ReportRecord, _, arg) => {
        const lessorVals = mapAndFormatDynamicValue(record, arg) as string[];
        const result = concatValuesBy(', ', lessorVals);
        return result;
      },
    }, // 203 - Lessor/Seller
  };
}

export const US_AVAILABILITY_COMMON_FOOTER_FIELDS = {
  204: { displayFn: displayFnWrapper }, // 204 - Notes
};
